// Pure helpers shared by the layouts + templates (ported from
// rensure-sme-smp lib/messaging/layout.ts and lib/messaging/tokens.ts).

/** Minimal HTML-entity escaping for interpolated values (customer data). */
export const escapeHtml = (value: string): string =>
  value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/** 1180 → "£1,180.00" */
export const formatMoney = (v: number): string =>
  `£${v.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** Renewal date, long form → "15 July 2026" */
export const longDate = (d: Date): string =>
  new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(d);

/** Renewal date, short form (SMS) → "15 Jul" */
export const shortDate = (d: Date): string =>
  new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", timeZone: "UTC" }).format(d);

/** The renewal anniversary — one year on (next-year win-back copy). */
export const anniversary = (renewalDate: Date): Date => {
  const d = new Date(renewalDate);
  d.setUTCFullYear(d.getUTCFullYear() + 1);
  return d;
};

/** Strip spaces so tel: links dial correctly. */
export const telHref = (callNumber: string): string => `tel:${callNumber.replace(/\s+/g, "")}`;

// Where the customer-facing app is hosted — the pay page lives there. Needs
// REACT_APP_API_ENDPOINT set in BOTH client and server envs (the client renders
// the same templates for the Message Templates preview).
const APP_BASE = (process.env.REACT_APP_API_ENDPOINT ?? "").replace(/\/+$/, "");

/** The customer pay page — same link as `payPageLink` in stripe-payment-controller. */
// The version reference rides as a query param: a policy reference can carry
// more than one chase, and the page has to open on the right one.
export const payHref = (policyRef: string, versionRef?: string): string =>
  `${APP_BASE}/pay/${encodeURIComponent(policyRef)}${versionRef ? `?version=${encodeURIComponent(versionRef)}` : ""}`;

export const round2 = (n: number): number => Math.round(n * 100) / 100;

const hexToRgb = (hex: string): [number, number, number] => {
  const m = hex.trim().replace(/^#/, "");
  const full = m.length === 3 ? m.split("").map((c) => c + c).join("") : m;
  const n = parseInt(full, 16);
  if (full.length !== 6 || Number.isNaN(n)) return [100, 116, 139];
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

/** Mix a brand colour toward white by `amount` (0–1). */
const mixWhite = (hex: string, amount: number): string => {
  const [r, g, b] = hexToRgb(hex);
  const mk = (c: number) => Math.round(c + (255 - c) * amount);
  const to2 = (c: number) => c.toString(16).padStart(2, "0");
  return `#${to2(mk(r))}${to2(mk(g))}${to2(mk(b))}`;
};

/** A very light accent-tinted fill (email-safe solid hex). */
export const tint = (primary: string): string => mixWhite(primary, 0.92);

/** A stronger accent tint for hairline borders / recommended rows. */
export const tintBorder = (primary: string): string => mixWhite(primary, 0.78);

/** Deterministic quiet colour pair for an insurer monogram, keyed by name. */
export const monogramColors = (name: string): { bg: string; fg: string } => {
  const palette = [
    { bg: "#e0e7ff", fg: "#4338ca" }, // indigo
    { bg: "#dcfce7", fg: "#15803d" }, // green
    { bg: "#fef3c7", fg: "#b45309" }, // amber
    { bg: "#ffe4e6", fg: "#be123c" }, // rose
    { bg: "#ccfbf1", fg: "#0f766e" }, // teal
    { bg: "#f3e8ff", fg: "#7e22ce" }, // violet
    { bg: "#e0f2fe", fg: "#0369a1" }, // sky
    { bg: "#ffedd5", fg: "#c2410c" }, // orange
  ];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return palette[h % palette.length];
};

/** First alphanumeric of the insurer name → monogram badge letter. */
export const initialOf = (name: string): string =>
  escapeHtml((name.match(/[A-Za-z0-9]/)?.[0] ?? "•").toUpperCase());
